import { formatNumber } from './number.js';

/**
 * Valutaformatering: "1.245.000,50 kr".
 *
 * Vi bruger ikke Intl med style:'currency', fordi den for da-DK/DKK giver "kr." med
 * afsluttende punktum i nogle ICU-versioner og placerer et haardt mellemrum vi ikke
 * kontrollerer. PRD kraever praecis "1.245.000,50 kr" - tal, ét mellemrum, suffix.
 * Derfor formateres tallet med da-DK og suffixet saettes selv.
 */

const SUFFIX_BY_CURRENCY: Record<string, string> = {
  DKK: 'kr',
};

export function formatCurrency(
  value: number | null | undefined,
  currency = 'DKK',
  options: { decimals?: number } = {},
): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const decimals = options.decimals ?? 2;
  const suffix = SUFFIX_BY_CURRENCY[currency] ?? currency;
  return `${formatNumber(value, decimals)} ${suffix}`;
}

/**
 * Kompakt visning til KPI-kort hvor pladsen er knap: "1,2 mio. kr", "845 t.kr".
 * Bruges kun hvor det praecise tal ogsaa er tilgaengeligt - fx som tooltip.
 */
export function formatCurrencyCompact(value: number | null | undefined, currency = 'DKK'): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const suffix = SUFFIX_BY_CURRENCY[currency] ?? currency;
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${formatNumber(value / 1_000_000, 1)} mio. ${suffix}`;
  if (abs >= 10_000) return `${formatNumber(value / 1000, 0)} t.${suffix}`;
  return formatCurrency(value, currency, { decimals: 0 });
}

/** Stoerrelsesordenen et beloeb vises i paa storskaermen. */
export type AmountScale = 'kr' | 'thousand' | 'million';

const AMOUNT_SCALES: Record<AmountScale, { divisor: number; decimals: number; unit: string }> = {
  kr: { divisor: 1, decimals: 0, unit: 'kr' },
  thousand: { divisor: 1000, decimals: 0, unit: 't.kr.' },
  million: { divisor: 1_000_000, decimals: 1, unit: 'mio.' },
};

/** Samme graenser som `formatCurrencyCompact`. */
export function amountScale(value: number): AmountScale {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return 'million';
  if (abs >= 10_000) return 'thousand';
  return 'kr';
}

/**
 * Et beloeb delt i tal og enhed - "5,1" og "mio." - til storskaermen, hvor enheden staar i en
 * mindre skrift end tallet. Uden `scale` vaelges den som `formatCurrencyCompact` goer.
 *
 * Enhederne er designtavlens: "t.kr." og "mio." uden "kr" efter. Skaermen viser kun kroner, og
 * paa tre meters afstand er det kortere det, der kan laeses.
 */
export function formatAmountParts(
  value: number | null | undefined,
  options: { scale?: AmountScale; decimals?: number } = {},
): { value: string; unit: string } {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return { value: '—', unit: '' };
  }
  const scale = AMOUNT_SCALES[options.scale ?? amountScale(value)];
  return {
    value: formatNumber(value / scale.divisor, options.decimals ?? scale.decimals),
    unit: scale.unit,
  };
}
